import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import api from "@/lib/api";
import { toast } from "sonner";
import { Gift, Send } from "lucide-react";

const VALUES = [10, 25, 50, 100];

export default function GiftCard() {
  const nav = useNavigate();
  const [amount, setAmount] = useState(25);
  const [form, setForm] = useState({
    purchaser_email: "", purchaser_name: "",
    recipient_email: "", recipient_name: "", message: "",
  });
  const [busy, setBusy] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    if (!form.purchaser_email || !form.recipient_email) return;
    setBusy(true);
    try {
      const { data } = await api.post("/gift-cards/checkout", {
        ...form, amount_eur: amount, origin_url: window.location.origin,
      });
      window.location.href = data.checkout_url;
    } catch (err) {
      toast.error(err.response?.data?.detail || "Gift card checkout failed");
      setBusy(false);
    }
  };

  const input = "w-full px-4 py-3 rounded-lg border border-[color:var(--brunchi-stone-2)] bg-white text-sm focus:outline-none focus:ring-2 focus:ring-[color:var(--brunchi-green)]";

  return (
    <div className="max-w-2xl mx-auto px-4 sm:px-6 lg:px-8 py-12" data-testid="gift-card-page">
      <div className="text-xs uppercase tracking-[0.25em] text-[color:var(--brunchi-green-hover)] mb-3">Share the good stuff</div>
      <h1 className="font-display text-4xl lg:text-5xl tracking-tight">Brunchi Gift Cards</h1>
      <p className="mt-3 text-[color:var(--brunchi-ink-soft)]">
        Delivered instantly to a recipient's inbox. Redeemable across the whole shop — protein bars,
        granola, sweets, standing orders. No expiry, no fine print.
      </p>

      <form onSubmit={submit} className="mt-8 space-y-4" data-testid="gift-form">
        <div>
          <div className="text-xs uppercase tracking-widest text-[color:var(--brunchi-ink-soft)] mb-2">Amount</div>
          <div className="grid grid-cols-4 gap-2">
            {VALUES.map((v) => (
              <button key={v} type="button" onClick={() => setAmount(v)}
                data-testid={`gift-amount-${v}`}
                className={`py-3 rounded-lg border-2 font-mono font-semibold ${
                  amount === v
                    ? "border-[color:var(--brunchi-green)] bg-[color:var(--brunchi-green-light)] text-[color:var(--brunchi-green-hover)]"
                    : "border-[color:var(--brunchi-stone-2)] bg-white"
                }`}>
                €{v}
              </button>
            ))}
          </div>
        </div>

        <div className="grid sm:grid-cols-2 gap-3">
          <input required type="email" placeholder="Your email" value={form.purchaser_email}
            onChange={(e) => setForm({ ...form, purchaser_email: e.target.value })} className={input} data-testid="gift-purchaser-email" />
          <input placeholder="Your name" value={form.purchaser_name}
            onChange={(e) => setForm({ ...form, purchaser_name: e.target.value })} className={input} data-testid="gift-purchaser-name" />
          <input required type="email" placeholder="Recipient email" value={form.recipient_email}
            onChange={(e) => setForm({ ...form, recipient_email: e.target.value })} className={input} data-testid="gift-recipient-email" />
          <input placeholder="Recipient name" value={form.recipient_name}
            onChange={(e) => setForm({ ...form, recipient_name: e.target.value })} className={input} data-testid="gift-recipient-name" />
        </div>
        <textarea placeholder="A short message (optional)" value={form.message}
          onChange={(e) => setForm({ ...form, message: e.target.value })} className={input} rows={3} data-testid="gift-message" />

        <button disabled={busy} type="submit"
          data-testid="gift-submit-btn"
          className="w-full py-3.5 rounded-full pill-primary font-medium inline-flex items-center justify-center gap-2 disabled:opacity-40">
          <Gift size={16} /> {busy ? "Redirecting to Stripe…" : `Send €${amount} gift`}
        </button>
        <p className="text-xs text-center text-[color:var(--brunchi-ink-soft)]">
          Payment via Stripe · code delivered by email to the recipient on successful payment.
        </p>
      </form>
    </div>
  );
}
